import { useState } from 'react';
import axios from 'axios';
import { useLang } from '../context/LangContext';
import './Partner.css';

const BenefitIcon = ({ type }) => {
  const icons = {
    broaden: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
      </svg>
    ),
    strengthen: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
        <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7z"/>
        <path d="M9 12l2 2 4-4"/>
      </svg>
    ),
    fuel: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
  };
  return icons[type] || null;
};

export default function Partner() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', type: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post('/api/leads', { ...form, source: 'partner' });
      setSent(true);
    } catch {
      // Still show success to user even if API fails
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const PARTNER_TYPES = [
    {
      icon: '🤝',
      titleKey: 'partner.reseller.title',
      descKey: 'partner.reseller.desc',
    },
    {
      icon: '⚙️',
      titleKey: 'partner.tech.title',
      descKey: 'partner.tech.desc',
    },
  ];

  const WHY = [
    { type: 'broaden',   titleKey: 'partner.why1.title', descKey: 'partner.why1.desc' },
    { type: 'strengthen', titleKey: 'partner.why2.title', descKey: 'partner.why2.desc' },
    { type: 'fuel',      titleKey: 'partner.why3.title', descKey: 'partner.why3.desc' },
  ];

  return (
    <main className="partner-page" dir={lang === 'ar' ? 'rtl' : 'ltr'}>

      {/* ── HERO ── */}
      <section className="partner-hero">
        <div className="partner-hero__bg" />
        <div className="container partner-hero__content">
          <p className="partner-badge">{t('partner.badge')}</p>
          <h1>
            {t('partner.title1')}<br />
            <span className="gradient-text">{t('partner.title2')}</span>
          </h1>
          <p className="partner-hero__sub">{t('partner.sub')}</p>
          <a href="#partner-form" className="cta-primary">{t('partner.cta')}</a>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="partner-intro">
        <div className="container partner-intro__inner">
          <div className="partner-intro__text">
            <h2>{t('partner.intro.title')}</h2>
            <p>{t('partner.intro.body')}</p>
          </div>
          <div className="partner-intro__visual">
            <div className="partner-stats">
              {[
                { v: '39+',  l: lang === 'ar' ? 'شريك نشط' : 'Active Partners' },
                { v: '14+',  l: lang === 'ar' ? 'متجر متكامل' : 'Integrated Stores' },
                { v: '99%',  l: lang === 'ar' ? 'رضا الشركاء' : 'Partner Satisfaction' },
                { v: '24h',  l: lang === 'ar' ? 'دعم مستمر' : 'Ongoing Support' },
              ].map((s, i) => (
                <div className="partner-stat" key={i}>
                  <strong>{s.v}</strong>
                  <span>{s.l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PARTNER TYPES ── */}
      <section className="partner-types">
        <div className="container">
          <div className="partner-types__grid">
            {PARTNER_TYPES.map((p, i) => (
              <div className="partner-type-card" key={i}>
                <div className="partner-type-card__icon">{p.icon}</div>
                <h2>{t(p.titleKey)}</h2>
                <p>{t(p.descKey)}</p>
                <a href="#partner-form" className="btn-outline-blue">{t('partner.signup')}</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY PARTNER ── */}
      <section className="partner-why">
        <div className="container">
          <h2 className="partner-why__title">
            {lang === 'ar'
              ? <>لماذا الشراكة مع <em>Route Rover؟</em></>
              : <>Why partner with <em>Route Rover?</em></>
            }
          </h2>
          <div className="partner-why__grid">
            {WHY.map((w, i) => (
              <div className="partner-why-card" key={i}>
                <h3>{t(w.titleKey)}</h3>
                <p>{t(w.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FORM ── */}
      <section className="partner-form-section" id="partner-form">
        <div className="container">
          <div className="partner-form-wrap">
            <div className="partner-form-header">
              <h2>{t('partner.form.title')}</h2>
              <p>{t('partner.form.sub')}</p>
            </div>

            {sent ? (
              <div className="partner-success">
                <div className="partner-success__icon">✓</div>
                <p>{t('partner.form.success')}</p>
              </div>
            ) : (
              <form className="partner-form" onSubmit={handleSubmit}>
                <div className="partner-form__row">
                  <div className="form-group">
                    <label>{t('partner.form.name')}</label>
                    <input type="text" required value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder={t('partner.form.name')} />
                  </div>
                  <div className="form-group">
                    <label>{t('partner.form.company')}</label>
                    <input type="text" required value={form.company}
                      onChange={e => setForm({ ...form, company: e.target.value })}
                      placeholder={t('partner.form.company')} />
                  </div>
                </div>
                <div className="partner-form__row">
                  <div className="form-group">
                    <label>{t('partner.form.email')}</label>
                    <input type="email" required value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="name@company.com" />
                  </div>
                  <div className="form-group">
                    <label>{t('partner.form.phone')}</label>
                    <input type="tel" value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      placeholder="+20 100 000 0000" />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('partner.form.type')}</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} required>
                    <option value="">—</option>
                    <option value="reseller">{t('partner.form.type.reseller')}</option>
                    <option value="tech">{t('partner.form.type.tech')}</option>
                    <option value="consultant">{t('partner.form.type.consultant')}</option>
                    <option value="other">{t('partner.form.type.other')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('partner.form.message')}</label>
                  <textarea rows="4" value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    placeholder={t('partner.form.message')} />
                </div>
                <button type="submit" className="cta-primary cta-primary--large" disabled={loading}>
                  {loading ? '...' : t('partner.form.submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

    </main>
  );
}
